/**
 * Custom Styled Google Map Component
 * 
 * This component demonstrates how to:
 * 1. Style Google Maps to appear "empty" with custom colors matching your site
 * 2. Add custom image overlays to highlight specific locations
 * 3. Use custom markers/icons
 * 
 * Based on the implementation seen at encompasscayman.com
 * 
 * The technique uses:
 * - Google Maps Styling API to hide labels, simplify colors, and match brand colors
 * - GroundOverlay to add custom images on top of the map
 * - Custom marker icons to match site design
 */

import { useEffect, useRef } from 'react';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';

export interface AmenityMarker {
  position: { lat: number; lng: number };
  icon: string; // URL to icon
  title: string;
  type: 'grocery' | 'gym' | 'cafe' | 'school' | 'service' | 'airport' | 'beach' | 'destination' | 'pearl';
  distance?: string;
  time?: string;
  infoWindow?: {
    title: string;
    content: string;
  };
}

export interface MapRef {
  zoomToLocation: (lat: number, lng: number, zoom?: number) => void;
  zoomToShowBoth: (from: { lat: number; lng: number }, to: { lat: number; lng: number }) => void;
  zoomToShowMultiple: (positions: { lat: number; lng: number }[]) => void;
  highlightMarker: (title: string) => void;
  showMarker: (title: string) => void;
  hideMarker: (title: string) => void;
  openInfoWindow: (title: string) => void;
  closeInfoWindow: () => void;
  resetView: () => void;
}

interface CustomStyledMapProps {
  center: { lat: number; lng: number };
  zoom?: number;
  overlayImageUrl?: string;
  overlayBounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  markerPosition?: { lat: number; lng: number }; // Legacy prop for backward compatibility
  customMarkerIcon?: string; // Legacy prop for backward compatibility
  markers?: AmenityMarker[]; // New prop for multiple markers
  visibleMarkers?: string[]; // Array of marker titles to show (if undefined, show all)
  areaHighlightBounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  areaHighlightColor?: string;
  areaHighlightOpacity?: number;
  apiKey: string; // Google Maps API key
  onMapReady?: (mapRef: MapRef) => void; // Callback when map is ready
  // Custom color theme to match your site
  themeColors?: {
    water?: string; // e.g., '#e8f4f8' for turquoise/beach theme
    land?: string; // e.g., '#f5f5f5' for light gray/beige
    roads?: string; // e.g., '#e0e0e0' for very light gray
  };
}

export function CustomStyledMap({
  center,
  zoom = 14,
  overlayImageUrl,
  overlayBounds,
  markerPosition,
  customMarkerIcon,
  markers = [],
  visibleMarkers,
  areaHighlightBounds,
  areaHighlightColor = '#567C8D', // brand-teal default
  areaHighlightOpacity = 0.25,
  apiKey,
  onMapReady,
  themeColors = {
    water: '#e8f4f8',
    land: '#f5f5f5',
    roads: '#e0e0e0',
  },
}: CustomStyledMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const overlayRef = useRef<google.maps.GroundOverlay | null>(null);
  const markerRefs = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const areaHighlightRef = useRef<google.maps.Rectangle | null>(null);
  const originalCenterRef = useRef<{ lat: number; lng: number }>(center);
  const originalZoomRef = useRef<number>(zoom);
  const pulseAnimationRef = useRef<number | null>(null);
  const markersCreatedRef = useRef<boolean>(false);

  // Load Google Maps API
  const { isLoaded, loadError } = useGoogleMaps({ apiKey });

  // Inject global CSS to hide Google Maps close buttons
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .gm-ui-hover-effect,
      .gm-style-iw-c button,
      button[aria-label*="Close"],
      button[title*="Close"] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstanceRef.current) return;

    // Initialize the map with custom styling
    const map = new google.maps.Map(mapRef.current, {
      center,
      zoom,
      // Disable default UI controls for cleaner look
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      // Apply custom styling to make map appear "empty" and match site colors
      styles: [
        // Hide all labels and text - this makes it look "empty"
        {
          featureType: 'all',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
        // Style water to match site colors (e.g., turquoise/beach theme)
        {
          featureType: 'water',
          elementType: 'geometry',
          stylers: [
            { color: themeColors.water },
            { saturation: -50 },
            { lightness: 50 },
          ],
        },
        // Style land/parks to be minimal
        {
          featureType: 'landscape',
          elementType: 'geometry',
          stylers: [
            { color: themeColors.land },
            { saturation: -100 },
            { lightness: 100 },
          ],
        },
        // Hide roads or style them minimally
        {
          featureType: 'road',
          elementType: 'geometry',
          stylers: [
            { color: themeColors.roads },
            { visibility: 'simplified' },
            { weight: 0.5 },
          ],
        },
        {
          featureType: 'road',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
        // Hide points of interest
        {
          featureType: 'poi',
          elementType: 'all',
          stylers: [{ visibility: 'off' }],
        },
        // Style administrative areas (borders) minimally
        {
          featureType: 'administrative',
          elementType: 'geometry',
          stylers: [
            { color: '#f0f0f0' },
            { visibility: 'simplified' },
            { weight: 0.5 },
          ],
        },
        {
          featureType: 'administrative',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
        // Make transit invisible
        {
          featureType: 'transit',
          elementType: 'all',
          stylers: [{ visibility: 'off' }],
        },
      ],
    });

    mapInstanceRef.current = map;

    // Add area highlight overlay (Grand Harbour area)
    if (areaHighlightBounds) {
      const rectangle = new google.maps.Rectangle({
        bounds: new google.maps.LatLngBounds(
          new google.maps.LatLng(areaHighlightBounds.south, areaHighlightBounds.west),
          new google.maps.LatLng(areaHighlightBounds.north, areaHighlightBounds.east)
        ),
        fillColor: areaHighlightColor,
        fillOpacity: areaHighlightOpacity,
        strokeColor: areaHighlightColor,
        strokeOpacity: Math.min(areaHighlightOpacity * 2, 0.8),
        strokeWeight: 3,
        map,
      });
      areaHighlightRef.current = rectangle;
    }

    // Add custom image overlay if provided
    // This is the key technique: overlay a custom image to highlight specific locations
    if (overlayImageUrl && overlayBounds) {
      const overlay = new google.maps.GroundOverlay(
        overlayImageUrl,
        new google.maps.LatLngBounds(
          new google.maps.LatLng(overlayBounds.south, overlayBounds.west),
          new google.maps.LatLng(overlayBounds.north, overlayBounds.east)
        ),
        {
          opacity: 0.8, // Adjust opacity to blend with map (0-1)
        }
      );
      overlay.setMap(map);
      overlayRef.current = overlay;
    }

    // Create info window for markers
    const infoWindow = new google.maps.InfoWindow();
    infoWindowRef.current = infoWindow;

    // Add multiple markers if provided
    // Only create markers if they haven't been created yet
    if (markers.length > 0) {
      // If markers already exist, skip creation and just update visibility
      if (markersCreatedRef.current && markerRefs.current.length > 0) {
        console.log('📍 Markers already exist, skipping creation. Updating visibility only.');
        // Update visibility based on visibleMarkers prop
        if (visibleMarkers !== undefined) {
          markerRefs.current.forEach((marker) => {
            const markerTitle = marker.getTitle();
            const markerData = markers.find((m) => m.title === markerTitle);
            const isPearl = markerData?.type === 'pearl';
            const shouldShow = isPearl || visibleMarkers.includes(markerTitle);
            marker.setMap(shouldShow ? map : null);
          });
        }
      } else {
        // Create new markers
        console.log('📍 Creating markers:', markers.length);
        console.log('   visibleMarkers prop:', visibleMarkers);
        console.log('   Markers already created:', markersCreatedRef.current);
        const newMarkers: google.maps.Marker[] = [];
        
        markers.forEach((markerData) => {
        console.log(`   Creating marker: "${markerData.title}" (type: ${markerData.type})`);
        // The Pearl gets a larger pin (56px vs 40px for others) - 40% bigger
        const isPearl = markerData.type === 'pearl';
        const pinSize = isPearl ? 56 : 40;
        const pinWidth = isPearl ? 44 : 32;
        const anchorX = isPearl ? 22 : 16;
        const anchorY = pinSize;
        
        // Determine if marker should be visible
        // If visibleMarkers is undefined, show all. Otherwise, only show markers in the array
        // The Pearl is always visible
        const shouldShow = isPearl || !visibleMarkers || visibleMarkers.includes(markerData.title);
        console.log(`     isPearl: ${isPearl}, shouldShow: ${shouldShow}, in visibleMarkers: ${visibleMarkers?.includes(markerData.title)}`);
        
        // Create marker with label positioned above the pin
        // labelOrigin is relative to icon's top-left (0,0), not the anchor
        // For a pin icon, we want label at top center, above the pin shape
        const marker = new google.maps.Marker({
          position: markerData.position,
          map: shouldShow ? map : null, // Only add to map if should be visible
          icon: {
            url: markerData.icon,
            scaledSize: new google.maps.Size(pinWidth, pinSize),
            anchor: new google.maps.Point(anchorX, anchorY), // Bottom center of pin
            labelOrigin: new google.maps.Point(anchorX, -15), // Position label above pin (negative Y moves up from icon top)
          },
          title: markerData.title,
          label: {
            text: markerData.title,
            color: '#1a1a1a',
            fontSize: isPearl ? '12px' : '11px',
            fontWeight: '600',
          },
          zIndex: isPearl ? google.maps.Marker.MAX_ZINDEX + 1 : undefined, // Ensure Pearl is on top
        });

        // Store marker reference for highlighting
        (marker as any).__originalIcon = markerData.icon;

        // Add click listener for info window
        if (markerData.infoWindow) {
          marker.addListener('click', () => {
            const content = `
              <div style="padding: 18px 20px; min-width: 240px; max-width: 300px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.5;">
                <h3 style="margin: 0 0 8px 0; font-size: 18px; font-weight: 600; color: #1a1a1a; letter-spacing: -0.01em;">
                  ${markerData.infoWindow.title}
                </h3>
                <p style="margin: 0 0 10px 0; font-size: 14px; color: #666; line-height: 1.6;">
                  ${markerData.infoWindow.content}
                </p>
                ${markerData.distance || markerData.time ? `
                  <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #e8e8e8; display: flex; flex-direction: column; gap: 6px;">
                    ${markerData.distance ? `<p style="margin: 0; font-size: 13px; color: #567C8D; font-weight: 500; display: flex; align-items: center; gap: 6px;"><span style="font-size: 12px;">📍</span> ${markerData.distance}</p>` : ''}
                    ${markerData.time ? `<p style="margin: 0; font-size: 13px; color: #567C8D; font-weight: 500; display: flex; align-items: center; gap: 6px;"><span style="font-size: 12px;">⏱️</span> ${markerData.time}</p>` : ''}
                  </div>
                ` : ''}
              </div>
            `;
            infoWindow.setContent(content);
            infoWindow.open(map, marker);
            
            // Style the info window close button after it opens
            const styleInfoWindow = () => {
              const infoWindowElement = document.querySelector('.gm-style-iw-c');
              if (infoWindowElement) {
                const closeButton = infoWindowElement.querySelector('.gm-ui-hover-effect') as HTMLElement;
                if (closeButton) {
                  closeButton.style.cssText = `
                    width: 20px !important;
                    height: 20px !important;
                    top: 6px !important;
                    right: 6px !important;
                    opacity: 0.5 !important;
                    transition: opacity 0.2s ease !important;
                    background: rgba(0, 0, 0, 0.05) !important;
                    border-radius: 4px !important;
                  `;
                  closeButton.addEventListener('mouseenter', () => {
                    closeButton.style.opacity = '0.8';
                  });
                  closeButton.addEventListener('mouseleave', () => {
                    closeButton.style.opacity = '0.5';
                  });
                }
                // Style the info window container
                (infoWindowElement as HTMLElement).style.cssText += `
                  border-radius: 8px !important;
                  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
                  padding: 0 !important;
                `;
              }
            };
            
            // Try multiple times with increasing delays to catch the DOM
            setTimeout(styleInfoWindow, 50);
            setTimeout(styleInfoWindow, 150);
            setTimeout(styleInfoWindow, 300);
            
            // Also use MutationObserver as fallback
            const observer = new MutationObserver(() => {
              styleInfoWindow();
            });
            observer.observe(document.body, { childList: true, subtree: true });
            setTimeout(() => observer.disconnect(), 2000);
            
            // Track marker click
            if (typeof window !== 'undefined' && (window as any).gtag) {
              (window as any).gtag('event', 'map_marker_click', {
                marker_title: markerData.title,
                marker_type: markerData.type,
              });
            }
          });
        }

        newMarkers.push(marker);
      });

      markerRefs.current = newMarkers;
      markersCreatedRef.current = true;
      console.log('✅ Created', newMarkers.length, 'markers');
      console.log('   Marker titles:', newMarkers.map(m => m.getTitle()));
      
      // Update marker visibility based on visibleMarkers prop
      if (visibleMarkers !== undefined) {
        console.log('👁️ Setting initial visibility based on visibleMarkers');
        markerRefs.current.forEach((marker) => {
          const markerTitle = marker.getTitle();
          const markerData = markers.find((m) => m.title === markerTitle);
          const isPearl = markerData?.type === 'pearl';
          const shouldShow = isPearl || visibleMarkers.includes(markerTitle);
          console.log(`   "${markerTitle}": shouldShow=${shouldShow}`);
          marker.setMap(shouldShow ? map : null);
        });
      } else {
        console.log('👁️ visibleMarkers is undefined, showing all markers');
      }
      }
    } else if (markerPosition) {
      // Legacy support: single marker
      const marker = new google.maps.Marker({
        position: markerPosition,
        map,
        icon: customMarkerIcon
          ? {
              url: customMarkerIcon,
              scaledSize: new google.maps.Size(40, 40),
              anchor: new google.maps.Point(20, 40),
            }
          : undefined,
      });
      markerRefs.current = [marker];
    }

    // Close info window when clicking on map
    map.addListener('click', () => {
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    });

    // Expose map control functions
    if (onMapReady) {
      const mapControl: MapRef = {
        zoomToLocation: (lat: number, lng: number, zoomLevel?: number) => {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter({ lat, lng });
            mapInstanceRef.current.setZoom(zoomLevel || 15);
          }
        },
        zoomToShowBoth: (from: { lat: number; lng: number }, to: { lat: number; lng: number }) => {
          console.log('🔍 zoomToShowBoth called');
          console.log('   From:', from);
          console.log('   To:', to);
          console.log('   Map instance:', !!mapInstanceRef.current);
          
          if (!mapInstanceRef.current) {
            console.error('❌ Map instance not available');
            return;
          }

          // Create bounds with both locations
          const bounds = new google.maps.LatLngBounds();
          const fromLatLng = new google.maps.LatLng(from.lat, from.lng);
          const toLatLng = new google.maps.LatLng(to.lat, to.lng);
          
          console.log('   From LatLng:', fromLatLng.toString());
          console.log('   To LatLng:', toLatLng.toString());
          
          bounds.extend(fromLatLng);
          bounds.extend(toLatLng);
          
          console.log('   Bounds:', bounds.toString());
          console.log('   Bounds NE:', bounds.getNorthEast().toString());
          console.log('   Bounds SW:', bounds.getSouthWest().toString());
          
          // Fit bounds to show both locations with padding
          // Use numeric padding (pixels) instead of object
          try {
            mapInstanceRef.current.fitBounds(bounds, 100);
            console.log('✅ fitBounds called successfully');
            
            // Log current map state after a brief delay
            setTimeout(() => {
              if (mapInstanceRef.current) {
                const center = mapInstanceRef.current.getCenter();
                const zoom = mapInstanceRef.current.getZoom();
                console.log('   Map center after zoom:', center?.toString());
                console.log('   Map zoom after zoom:', zoom);
              }
            }, 300);
          } catch (error) {
            console.error('❌ Error in fitBounds:', error);
          }
        },
        zoomToShowMultiple: (positions: { lat: number; lng: number }[]) => {
          console.log('🔍 zoomToShowMultiple called');
          console.log('   Positions count:', positions.length);
          console.log('   Map instance:', !!mapInstanceRef.current);
          
          if (!mapInstanceRef.current || positions.length === 0) {
            console.error('❌ Map instance not available or no positions');
            return;
          }

          // Create bounds with all positions
          const bounds = new google.maps.LatLngBounds();
          positions.forEach((pos) => {
            bounds.extend(new google.maps.LatLng(pos.lat, pos.lng));
          });
          
          console.log('   Bounds:', bounds.toString());
          
          // Fit bounds to show all locations with padding
          try {
            mapInstanceRef.current.fitBounds(bounds, 100);
            console.log('✅ fitBounds called successfully for multiple positions');
          } catch (error) {
            console.error('❌ Error in fitBounds:', error);
          }
        },
        showMarker: (title: string) => {
          console.log('🔍 showMarker called for:', title);
          console.log('   Map instance:', !!mapInstanceRef.current);
          console.log('   Total markers:', markerRefs.current.length);
          console.log('   Marker titles:', markerRefs.current.map(m => m.getTitle()));
          
          if (!mapInstanceRef.current) {
            console.error('❌ Map instance not available');
            return;
          }
          
          let found = false;
          markerRefs.current.forEach((marker) => {
            const markerTitle = marker.getTitle();
            console.log('   Checking marker:', markerTitle, 'matches?', markerTitle === title);
            if (markerTitle === title) {
              console.log('✅ Showing marker:', title);
              marker.setMap(mapInstanceRef.current);
              // Ensure marker is visible by bringing it to front
              marker.setZIndex(google.maps.Marker.MAX_ZINDEX);
              found = true;
            }
          });
          
          if (!found) {
            console.error('❌ Marker not found in markerRefs:', title);
          }
        },
        hideMarker: (title: string) => {
          markerRefs.current.forEach((marker) => {
            if (marker.getTitle() === title) {
              marker.setMap(null);
            }
          });
        },
        highlightMarker: (title: string) => {
          // Clear any existing pulse animation
          if (pulseAnimationRef.current) {
            clearInterval(pulseAnimationRef.current);
            pulseAnimationRef.current = null;
          }

          markerRefs.current.forEach((marker) => {
            if (marker.getTitle() === title) {
              const markerData = markers.find((m) => m.title === title);
              const isPearl = markerData?.type === 'pearl';
              const baseSize = isPearl ? 56 : 40;
              const baseWidth = isPearl ? 44 : 32;
              
              // Get original icon
              const icon = marker.getIcon() as google.maps.Icon;
              if (!icon || typeof icon !== 'object' || !('url' in icon)) return;

              // Pulse animation: scale up and down 3 times
              let pulseCount = 0;
              const maxPulses = 3;
              const pulseDuration = 1500; // 1.5 seconds total
              const pulseInterval = pulseDuration / (maxPulses * 2); // Up and down per pulse

              const pulse = () => {
                if (pulseCount >= maxPulses * 2) {
                  // Reset to original size
                  marker.setIcon({
                    ...icon,
                    scaledSize: new google.maps.Size(baseWidth, baseSize),
                  });
                  if (pulseAnimationRef.current) {
                    clearInterval(pulseAnimationRef.current);
                    pulseAnimationRef.current = null;
                  }
                  return;
                }

                // Alternate between larger and normal size
                const isUp = pulseCount % 2 === 0;
                const scale = isUp ? 1.4 : 1.0; // 40% larger when pulsing up
                
                marker.setIcon({
                  ...icon,
                  scaledSize: new google.maps.Size(
                    Math.round(baseWidth * scale),
                    Math.round(baseSize * scale)
                  ),
                });

                pulseCount++;
              };

              // Start pulsing
              pulseAnimationRef.current = window.setInterval(pulse, pulseInterval);

              // Open info window
              if (markerData?.infoWindow && infoWindowRef.current) {
                const content = `
                  <div style="padding: 18px 20px; min-width: 240px; max-width: 300px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.5;">
                    <h3 style="margin: 0 0 8px 0; font-size: 18px; font-weight: 600; color: #1a1a1a; letter-spacing: -0.01em;">
                      ${markerData.infoWindow.title}
                    </h3>
                    <p style="margin: 0 0 10px 0; font-size: 14px; color: #666; line-height: 1.6;">
                      ${markerData.infoWindow.content}
                    </p>
                    ${markerData.distance || markerData.time ? `
                      <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #e8e8e8; display: flex; flex-direction: column; gap: 6px;">
                        ${markerData.distance ? `<p style="margin: 0; font-size: 13px; color: #D4AF37; font-weight: 500; display: flex; align-items: center; gap: 6px;"><span style="font-size: 12px;">📍</span> ${markerData.distance}</p>` : ''}
                        ${markerData.time ? `<p style="margin: 0; font-size: 13px; color: #D4AF37; font-weight: 500; display: flex; align-items: center; gap: 6px;"><span style="font-size: 12px;">⏱️</span> ${markerData.time}</p>` : ''}
                      </div>
                    ` : ''}
                  </div>
                `;
                infoWindowRef.current.setContent(content);
                infoWindowRef.current.open(mapInstanceRef.current, marker);
                
            // Hide close button and style info window after it opens
            const styleInfoWindow = () => {
              const infoWindowElement = document.querySelector('.gm-style-iw-c');
              if (infoWindowElement) {
                // Hide close button completely
                const selectors = [
                  '.gm-ui-hover-effect',
                  'button[aria-label*="Close"]',
                  'button[title*="Close"]',
                  '.gm-style-iw-c button',
                ];
                
                for (const selector of selectors) {
                  const closeButton = infoWindowElement.querySelector(selector) as HTMLElement;
                  if (closeButton) {
                    closeButton.style.cssText = 'display: none !important;';
                    break;
                  }
                }

                // Style the info window container
                (infoWindowElement as HTMLElement).style.cssText += `
                  border-radius: 8px !important;
                  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
                  padding: 0 !important;
                `;
              }
            };
            
            // Try multiple times with increasing delays
            setTimeout(styleInfoWindow, 50);
            setTimeout(styleInfoWindow, 150);
            setTimeout(styleInfoWindow, 300);
            setTimeout(styleInfoWindow, 500);
            
            // Use MutationObserver as fallback
            const observer = new MutationObserver(() => {
              styleInfoWindow();
            });
            observer.observe(document.body, { childList: true, subtree: true });
            setTimeout(() => observer.disconnect(), 2000);
              }
            }
          });
        },
        openInfoWindow: (title: string) => {
          if (!mapInstanceRef.current || !infoWindowRef.current) {
            console.error('❌ Map instance or info window not available');
            return;
          }

          // Find the marker by title
          const marker = markerRefs.current.find((m) => m.getTitle() === title);
          if (!marker) {
            console.error('❌ Marker not found:', title);
            return;
          }

          // Find marker data to get info window content
          const markerData = markers.find((m) => m.title === title);
          if (!markerData?.infoWindow) {
            console.warn('⚠️ No info window data for marker:', title);
            return;
          }

          // Create info window content
          const content = `
            <div style="padding: 18px 20px; min-width: 240px; max-width: 300px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.5;">
              <h3 style="margin: 0 0 8px 0; font-size: 18px; font-weight: 600; color: #1a1a1a; letter-spacing: -0.01em;">
                ${markerData.infoWindow.title}
              </h3>
              <p style="margin: 0 0 10px 0; font-size: 14px; color: #666; line-height: 1.6;">
                ${markerData.infoWindow.content}
              </p>
              ${markerData.distance || markerData.time ? `
                <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #e8e8e8; display: flex; flex-direction: column; gap: 6px;">
                  ${markerData.distance ? `<p style="margin: 0; font-size: 13px; color: #D4AF37; font-weight: 500; display: flex; align-items: center; gap: 6px;"><span style="font-size: 12px;">📍</span> ${markerData.distance}</p>` : ''}
                  ${markerData.time ? `<p style="margin: 0; font-size: 13px; color: #D4AF37; font-weight: 500; display: flex; align-items: center; gap: 6px;"><span style="font-size: 12px;">⏱️</span> ${markerData.time}</p>` : ''}
                </div>
              ` : ''}
            </div>
          `;

          infoWindowRef.current.setContent(content);
          infoWindowRef.current.open(mapInstanceRef.current, marker);

          // Hide close button after opening
          const hideCloseButton = () => {
            const selectors = [
              '.gm-ui-hover-effect',
              'button[aria-label*="Close"]',
              'button[title*="Close"]',
              '.gm-style-iw-c button',
            ];
            
            for (const selector of selectors) {
              const closeButton = document.querySelector(selector) as HTMLElement;
              if (closeButton) {
                closeButton.style.cssText = 'display: none !important;';
                break;
              }
            }
          };

          // Try multiple times to catch the DOM
          setTimeout(hideCloseButton, 50);
          setTimeout(hideCloseButton, 150);
          setTimeout(hideCloseButton, 300);
          setTimeout(hideCloseButton, 500);

          // Use MutationObserver as fallback
          const observer = new MutationObserver(() => {
            hideCloseButton();
          });
          observer.observe(document.body, { childList: true, subtree: true });
          setTimeout(() => observer.disconnect(), 2000);
        },
        closeInfoWindow: () => {
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }
        },
        resetView: () => {
          if (mapInstanceRef.current) {
            // Clear any pulse animations
            if (pulseAnimationRef.current) {
              clearInterval(pulseAnimationRef.current);
              pulseAnimationRef.current = null;
            }
            
            mapInstanceRef.current.setCenter(originalCenterRef.current);
            mapInstanceRef.current.setZoom(originalZoomRef.current);
            
            if (infoWindowRef.current) {
              infoWindowRef.current.close();
            }
          }
        },
      };
      onMapReady(mapControl);
    }

    // Store original center and zoom
    originalCenterRef.current = center;
    originalZoomRef.current = zoom;

    // Cleanup function
    return () => {
      console.log('🧹 Cleanup function called');
      console.log('   Markers created:', markersCreatedRef.current);
      console.log('   Marker count:', markerRefs.current.length);
      
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
      }
      if (areaHighlightRef.current) {
        areaHighlightRef.current.setMap(null);
      }
      if (pulseAnimationRef.current) {
        clearInterval(pulseAnimationRef.current);
        pulseAnimationRef.current = null;
      }
      
      // Only clear markers if we're actually recreating the map instance
      // Don't clear markers if the map instance is still the same
      // (markers array reference might change but map instance stays the same)
      if (markersCreatedRef.current && markerRefs.current.length > 0 && mapInstanceRef.current) {
        console.log('⚠️ Skipping marker cleanup - markers already exist and map instance unchanged');
        // Don't clear markers - they should persist
        // Only clear overlays and animations
        return;
      }
      
      // Only clear markers if map is being destroyed
      if (markerRefs.current.length > 0) {
        console.log('🧹 Clearing markers (map being recreated)');
        markerRefs.current.forEach((marker) => {
          marker.setMap(null);
        });
        markerRefs.current = [];
        markersCreatedRef.current = false;
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
  }, [
    isLoaded,
    center,
    zoom,
    overlayImageUrl,
    overlayBounds,
    markerPosition,
    customMarkerIcon,
    markers,
    // visibleMarkers removed - handled in separate useEffect
    areaHighlightBounds,
    areaHighlightColor,
    areaHighlightOpacity,
    onMapReady,
    themeColors,
  ]);

  // Update marker visibility when visibleMarkers changes
  useEffect(() => {
    console.log('🔄 visibleMarkers useEffect triggered');
    console.log('   visibleMarkers:', visibleMarkers);
    console.log('   Map instance:', !!mapInstanceRef.current);
    console.log('   Marker count:', markerRefs.current.length);
    
    if (!mapInstanceRef.current) {
      console.warn('⚠️ Map instance not ready');
      return;
    }
    
    if (markerRefs.current.length === 0) {
      console.warn('⚠️ No markers in markerRefs');
      return;
    }
    
    if (visibleMarkers !== undefined) {
      console.log('👁️ Updating marker visibility based on visibleMarkers array');
      markerRefs.current.forEach((marker) => {
        const markerTitle = marker.getTitle();
        const markerData = markers.find((m) => m.title === markerTitle);
        const isPearl = markerData?.type === 'pearl';
        const shouldShow = isPearl || visibleMarkers.includes(markerTitle);
        console.log(`   Marker "${markerTitle}": isPearl=${isPearl}, shouldShow=${shouldShow}, inArray=${visibleMarkers.includes(markerTitle)}`);
        marker.setMap(shouldShow ? mapInstanceRef.current : null);
      });
    } else {
      console.log('👁️ Showing all markers (visibleMarkers is undefined)');
      // If visibleMarkers is undefined, show all markers
      markerRefs.current.forEach((marker) => {
        marker.setMap(mapInstanceRef.current);
      });
    }
  }, [visibleMarkers, markers]);

  if (loadError) {
    return (
      <div className="w-full h-full min-h-[400px] rounded-lg bg-muted flex items-center justify-center">
        <p className="text-muted-foreground">Error loading map: {loadError.message}</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-full min-h-[400px] rounded-lg bg-muted flex items-center justify-center">
        <p className="text-muted-foreground">Loading map...</p>
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      className="w-full h-full min-h-[400px] rounded-lg overflow-hidden"
      style={{ minHeight: '400px' }}
    />
  );
}

/**
 * Example usage:
 * 
 * // 1. Get your Google Maps API key from Google Cloud Console
 * // 2. Create a custom image overlay (PNG/SVG) that highlights your location
 * // 3. Determine the bounds (north, south, east, west) for your overlay
 * 
 * <CustomStyledMap
 *   apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
 *   center={{ lat: 19.3133, lng: -81.2546 }} // Grand Cayman coordinates
 *   zoom={15}
 *   overlayImageUrl="/images/map-highlight.png" // Custom image to overlay
 *   overlayBounds={{
 *     north: 19.3150,
 *     south: 19.3115,
 *     east: -81.2530,
 *     west: -81.2560,
 *   }}
 *   markerPosition={{ lat: 19.3133, lng: -81.2546 }}
 *   customMarkerIcon="/map-pin.svg" // Custom marker icon
 *   themeColors={{
 *     water: '#e8f4f8', // Match your site's color scheme
 *     land: '#f5f5f5',
 *     roads: '#e0e0e0',
 *   }}
 * />
 * 
 * How it works:
 * 
 * 1. STYLING: The `styles` array hides labels, simplifies colors, and matches
 *    your brand colors. This makes the map look "empty" and branded.
 * 
 * 2. OVERLAY: The GroundOverlay adds a custom image on top of the map. This
 *    image can highlight specific areas, show property boundaries, or add
 *    custom graphics that match your site design.
 * 
 * 3. MARKER: Custom marker icons replace the default red pin with your own
 *    SVG/PNG that matches your site's design.
 * 
 * Tips:
 * - Use a semi-transparent PNG for the overlay to blend with the map
 * - Adjust overlay opacity (0-1) to control how much map shows through
 * - Match theme colors to your site's color palette
 * - Use SVG markers for crisp rendering at any zoom level
 */

